// Reddit API service for frontend integration
import { supabase } from '@/integrations/supabase/client'
import type { Tables, TablesInsert, TablesUpdate } from '@/integrations/supabase/types'

// Type aliases for easier use
export type RedditAccount = Tables<'reddit_accounts'>
export type ApprovedSubreddit = Tables<'approved_subreddits'>
export type ScheduledRedditPost = Tables<'scheduled_reddit_posts'>
export type RedditPostingSlot = Tables<'reddit_posting_slots'>
export type RedditAgentLog = Tables<'reddit_agent_log'>
export type SubredditSearchCache = Tables<'subreddit_search_cache'>

// Insert types
export type RedditAccountInsert = TablesInsert<'reddit_accounts'>
export type ApprovedSubredditInsert = TablesInsert<'approved_subreddits'>
export type ScheduledRedditPostInsert = TablesInsert<'scheduled_reddit_posts'>
export type RedditPostingSlotInsert = TablesInsert<'reddit_posting_slots'>

// Update types
export type RedditAccountUpdate = TablesUpdate<'reddit_accounts'>
export type ScheduledRedditPostUpdate = TablesUpdate<'scheduled_reddit_posts'>

// Custom interfaces for API responses
export interface RedditOAuthResponse {
  access_token: string
  refresh_token: string
  expires_in: number
  scope: string
  token_type: string
}

export interface SubredditSearchResult {
  id: string
  name: string
  display_name: string
  title: string
  description: string
  subscribers: number
  created_utc: number
  over18: boolean
  public_description: string
  difficulty_score?: number
  can_post?: boolean
  karma_required?: number
  account_age_required?: number
}

export interface RedditPostResponse {
  success: boolean
  data?: {
    id: string
    url: string
    permalink: string
  }
  error?: string
}

// Edge Function Interfaces
export interface EdgeFunctionResponse {
  success: boolean
  data?: any
  error?: string
}

// Agent Decision Interface
export interface AgentDecision {
  action: 'post' | 'skip' | 'reschedule'
  reasoning: string
  selected_subreddit?: string
  confidence_score: number
  factors_considered: string[]
}

// Reddit OAuth functions
export const redditAuth = {
  /**
   * Initiate Reddit OAuth flow
   */
  async initiateOAuth(projectId: string): Promise<{ authUrl: string; state: string }> {
    const state = crypto.randomUUID()
    const clientId = import.meta.env.VITE_REDDIT_CLIENT_ID
    const redirectUri = import.meta.env.VITE_REDDIT_REDIRECT_URI || `${window.location.origin}/auth/reddit/callback`
    
    const authUrl = `https://www.reddit.com/api/v1/authorize?` +
      `client_id=${clientId}&` +
      `response_type=code&` +
      `state=${state}&` +
      `redirect_uri=${encodeURIComponent(redirectUri)}&` +
      `duration=permanent&` +
      `scope=identity,submit,read,mysubreddits`
    
    // Store state and projectId for callback verification
    localStorage.setItem('reddit_oauth_state', state)
    localStorage.setItem('reddit_oauth_project_id', projectId)
    
    return { authUrl, state }
  },

  /**
   * Handle OAuth callback and exchange code for tokens
   */
  async handleCallback(code: string, state: string, projectId: string): Promise<RedditAccount> {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) throw new Error('User not authenticated')

    const { data, error } = await supabase.functions.invoke('reddit-oauth-callback', {
      body: {
        code,
        state,
        projectId,
        userId: user.id
      }
    })

    if (error) {
      console.error('Supabase error:', error);
      console.error('Error message:', error.message);
      console.error('Error code:', error.code);
      console.error('Error details:', error.details);
      console.error('Error hint:', error.hint);
      throw error
    }
    if (data.status === 'error') throw new Error(data.message)

    return data.data
  },

  /**
   * Get Reddit account for project
   */
  async getAccount(projectId: string): Promise<RedditAccount | null> {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) throw new Error('User not authenticated')

    const { data, error } = await supabase
      .from('reddit_accounts')
      .select('*')
      .eq('user_id', user.id)
      .eq('project_id', projectId)
      .single()

    if (error && error.code !== 'PGRST116') throw error
    return data
  },

  /**
   * Disconnect Reddit account
   */
  async disconnect(projectId: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) throw new Error('User not authenticated')

    const { error } = await supabase
      .from('reddit_accounts')
      .delete()
      .eq('user_id', user.id)
      .eq('project_id', projectId)

    if (error) throw error
  }
}

// Subreddit management functions
export const subredditManager = {
  /**
   * Search for subreddits by keyword
   */
  async searchSubreddits(keyword: string, projectId: string): Promise<SubredditSearchResult[]> {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) throw new Error('User not authenticated')

    const { data, error } = await supabase.functions.invoke('reddit-search-subreddits', {
      body: {
        keyword,
        projectId,
        userId: user.id
      }
    })

    if (error) throw error
    if (data.status === 'error') throw new Error(data.message)

    return data.data.subreddits
  },

  /**
   * Validate subreddit access
   */
  async validateSubreddit(subredditName: string, projectId: string, autoSubscribe = false): Promise<any> {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) throw new Error('User not authenticated')

    const { data, error } = await supabase.functions.invoke('reddit-validate-subreddit-access', {
      body: {
        subredditName,
        projectId,
        userId: user.id,
        autoSubscribe
      }
    })

    if (error) throw error
    if (data.status === 'error') throw new Error(data.message)

    return data.data
  },

  /**
   * Get approved subreddits for project
   */
  async getApprovedSubreddits(projectId: string): Promise<ApprovedSubreddit[]> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('approved_subreddits')
      .select('*')
      .eq('project_id', projectId)
      .eq('user_id', user.id)
      .order('added_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  /**
   * Add subreddit to approved list
   */
  async addApprovedSubreddit(subredditName: string, projectId: string): Promise<ApprovedSubreddit> {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) throw new Error('User not authenticated')

    const { data, error } = await supabase
      .from('approved_subreddits')
      .insert({
        user_id: user.id,
        project_id: projectId,
        subreddit_name: subredditName
      })
      .select()
      .single()

    if (error) throw error
    return data
  },

  /**
   * Remove subreddit from approved list
   */
  async removeApprovedSubreddit(subredditId: string): Promise<void> {
    const { error } = await supabase
      .from('approved_subreddits')
      .delete()
      .eq('id', subredditId)

    if (error) throw error
  }
}

// Post scheduling functions
export const postScheduler = {
  /**
   * Get scheduled posts for project
   */
  async getScheduledPosts(projectId: string): Promise<ScheduledRedditPost[]> {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) throw new Error('User not authenticated')

    const { data, error } = await supabase
      .from('scheduled_reddit_posts')
      .select('*')
      .eq('user_id', user.id)
      .eq('project_id', projectId)
      .order('scheduled_at_utc', { ascending: true })

    if (error) throw error
    return data || []
  },

  /**
   * Create scheduled post
   */
  async createScheduledPost(
    projectId: string,
    subredditName: string,
    title: string,
    content: string,
    scheduledAt: string
  ): Promise<ScheduledRedditPost> {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) throw new Error('User not authenticated')

    const { data, error } = await supabase
      .from('scheduled_reddit_posts')
      .insert({
        user_id: user.id,
        project_id: projectId,
        subreddit_name: subredditName,
        post_title: title, // Database uses post_title, not title
        post_content: content, // Database uses post_content, not content
        scheduled_at_utc: scheduledAt, // Database uses scheduled_at_utc, not scheduled_for
        status: 'approved_for_publishing' // Fix: Manual scheduling implies approval
      })
      .select()
      .single()

    if (error) {
      console.error('Supabase error:', error);
      console.error('Error message:', error.message);
      console.error('Error code:', error.code);
      console.error('Error details:', error.details);
      console.error('Error hint:', error.hint);
      throw error
    }
    return data
  },

  /**
   * Update post status
   */
  async updatePostStatus(postId: string, status: ScheduledRedditPost['status']): Promise<void> {
    const { error } = await supabase
      .from('scheduled_reddit_posts')
      .update({ status, updated_at: new Date().toISOString() })
      .eq('id', postId)

    if (error) throw error
  },

  /**
   * Delete scheduled post
   */
  async deleteScheduledPost(postId: string): Promise<void> {
    const { error } = await supabase
      .from('scheduled_reddit_posts')
      .delete()
      .eq('id', postId)

    if (error) throw error
  },


}

// Posting time slots functions
export const postingSlots = {
  /**
   * Get posting slots for project
   */
  async getPostingSlots(projectId: string): Promise<any[]> {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) throw new Error('User not authenticated')

    const { data, error } = await supabase
      .from('reddit_posting_slots')
      .select('*')
      .eq('project_id', projectId)
      .order('day_of_week', { ascending: true })
      .order('time_slot_start_utc', { ascending: true })

    if (error) throw error
    return data || []
  },

  /**
   * Create posting slot
   */
  async createPostingSlot(projectId: string, dayOfWeek: number, hourUtc: number): Promise<RedditPostingSlot> {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) throw new Error('User not authenticated')

    const { data, error } = await supabase
      .from('reddit_posting_slots')
      .insert({
        project_id: projectId,
        day_of_week: dayOfWeek,
        time_slot_start_utc: `${hourUtc.toString().padStart(2, '0')}:00:00`, // Database expects time format
        is_active: true // Database uses is_active, not enabled
      })
      .select()
      .single()

    if (error) {
      console.error('Supabase error:', error);
      console.error('Error message:', error.message);
      console.error('Error code:', error.code);
      console.error('Error details:', error.details);
      console.error('Error hint:', error.hint);
      throw error
    }
    return data
  },

  /**
   * Update posting slot
   */
  async updatePostingSlot(slotId: string, isActive: boolean): Promise<void> {
    const { error } = await supabase
      .from('reddit_posting_slots')
      .update({ is_active: isActive })
      .eq('id', slotId)

    if (error) throw error
  },

  /**
   * Delete posting slot
   */
  async deletePostingSlot(slotId: string): Promise<void> {
    const { error } = await supabase
      .from('reddit_posting_slots')
      .delete()
      .eq('id', slotId)

    if (error) throw error
  }
}

// Reddit agent functions
export const redditAgent = {
  /**
   * Run Reddit agent for project
   */
  async runAgent(projectId: string): Promise<any> {
    const { data, error } = await supabase.functions.invoke('reddit-agent-runner', {
      body: { projectId }
    })

    if (error) throw error
    if (data.status === 'error') throw new Error(data.message)

    return data.data
  },

  /**
   * Get agent logs for project
   */
  async getAgentLogs(projectId: string, limit = 50): Promise<any[]> {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) throw new Error('User not authenticated')

    const { data, error } = await supabase
      .from('reddit_agent_log')
      .select('*')
      .eq('project_id', projectId)
      .order('run_timestamp_utc', { ascending: false })
      .limit(limit)

    if (error) throw error
    return data || []
  },

  /**
   * Get agent status for project
   */
  async getAgentStatus(projectId: string): Promise<any> {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) throw new Error('User not authenticated')

    // Get project settings
    const { data: project, error: projectError } = await supabase
      .from('projects')
      .select('enable_reddit_agent, reddit_agent_paused')
      .eq('id', projectId)
      .single()

    if (projectError) throw projectError

    // Get recent logs for metrics
    const logs = await this.getAgentLogs(projectId, 100)
    const scheduledPosts = await postScheduler.getScheduledPosts(projectId)

    // Calculate metrics
    const postsGenerated = logs.filter(log => log.action_type === 'content_generated_and_queued').length
    const postsPublished = scheduledPosts.filter(post => post.status === 'sent').length
    const successRate = postsGenerated > 0 ? (postsPublished / postsGenerated) * 100 : 0

    return {
      isActive: project.enable_reddit_agent && !project.reddit_agent_paused,
      postsGenerated,
      postsPublished,
      successRate,
      postsAwaitingApproval: scheduledPosts.filter(post => post.status === 'pending').length,
      lastRun: logs.length > 0 ? logs[0].created_at : null
    }
  },

  /**
   * Toggle agent status
   */
  async toggleAgent(projectId: string, enabled: boolean): Promise<void> {
    const { error } = await supabase
      .from('projects')
      .update({ enable_reddit_agent: enabled })
      .eq('id', projectId)

    if (error) throw error
  }
}

// Project settings functions
export const projectSettings = {
  /**
   * Get Reddit settings for project
   */
  async getRedditSettings(projectId: string): Promise<any> {
    const { data, error } = await supabase
      .from('projects')
      .select('enable_reddit_agent, reddit_agent_paused, reddit_require_approval, reddit_auto_subscribe')
      .eq('id', projectId)
      .single()

    if (error) throw error
    return data
  },

  /**
   * Update Reddit settings for project
   */
  async updateRedditSettings(projectId: string, settings: any): Promise<void> {
    const { error } = await supabase
      .from('projects')
      .update(settings)
      .eq('id', projectId)

    if (error) throw error
  }
}

// Reddit Account Management
export const getRedditAccount = async (projectId: string) => {
  const { data, error } = await supabase
    .from('reddit_accounts')
    .select('*')
    .eq('project_id', projectId)
    .single();

  if (error && error.code !== 'PGRST116') {
    throw error;
  }

  return data;
};

export const saveRedditTokens = async (
  projectId: string,
  userId: string,
  tokens: {
    access_token: string;
    refresh_token: string;
    expires_at: string;
    reddit_username?: string;
  }
) => {
  const { data, error } = await supabase
    .from('reddit_accounts')
    .upsert({
      project_id: projectId,
      user_id: userId,
      access_token: tokens.access_token,
      refresh_token: tokens.refresh_token,
      expires_at: tokens.expires_at,
      reddit_username: tokens.reddit_username || 'unknown',
      // Removed scope and token_type as they're not in the database schema
    })
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const deleteRedditAccount = async (projectId: string) => {
  const { error } = await supabase
    .from('reddit_accounts')
    .delete()
    .eq('project_id', projectId);

  if (error) throw error;
};

// Subreddit Management
export const getApprovedSubreddits = async (projectId: string) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  const { data, error } = await supabase
    .from('approved_subreddits')
    .select('*')
    .eq('project_id', projectId)
    .eq('user_id', user.id)
    .order('added_at', { ascending: false });

  if (error) throw error;
  return data || [];
};

export const addApprovedSubreddit = async (subredditData: ApprovedSubredditInsert) => {
  const { data, error } = await supabase
    .from('approved_subreddits')
    .insert(subredditData)
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const removeApprovedSubreddit = async (subredditId: string) => {
  const { error } = await supabase
    .from('approved_subreddits')
    .delete()
    .eq('id', subredditId);

  if (error) throw error;
};

// Post Scheduling
export const getScheduledPosts = async (projectId: string, userId?: string) => {
  try {
    // Get user ID if not provided
    let userIdToUse = userId;
    if (!userIdToUse) {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');
      userIdToUse = user.id;
    }

    const { data, error } = await supabase
      .from('scheduled_reddit_posts')
      .select('*')
      .eq('project_id', projectId)
      .eq('user_id', userIdToUse) // Add user authentication
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching scheduled posts:', error);
    throw error;
  }
};

export const scheduleRedditPost = async (postData: {
  project_id: string;
  user_id: string;
  title: string;
  content: string;
  subreddit_name: string;
  scheduled_for: string;
}) => {
  console.log('scheduleRedditPost called with:', postData);
  
  const insertData = {
    project_id: postData.project_id,
    user_id: postData.user_id,
    post_title: postData.title, // Use correct database field name
    post_content: postData.content, // Use correct database field name
    subreddit_name: postData.subreddit_name,
    scheduled_at_utc: postData.scheduled_for, // Use correct database field name
    status: 'approved_for_publishing', // Fix: Manual scheduling implies approval
    retry_count: 0, // Use correct database field name
  };
  
  console.log('Inserting data:', insertData);

  const { data, error } = await supabase
    .from('scheduled_reddit_posts')
    .insert(insertData)
    .select()
    .single();

  if (error) {
    console.error('Supabase error:', error);
    console.error('Error message:', error.message);
    console.error('Error code:', error.code);
    console.error('Error details:', error.details);
    console.error('Error hint:', error.hint);
    throw error;
  }
  
  console.log('Successfully created scheduled post:', data);
  return data;
};

export const updatePostStatus = async (
  postId: string,
  updates: {
    status?: string;
    reddit_post_id?: string;
    error_message?: string;
    retry_count?: number;
    last_attempted_at_utc?: string;
    upvotes?: number;
    comment_count?: number;
    preferred_flair?: string | null;
  }
) => {
  const { data, error } = await supabase
    .from('scheduled_reddit_posts')
    .update(updates)
    .eq('id', postId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const deleteScheduledPost = async (postId: string) => {
  const { error } = await supabase
    .from('scheduled_reddit_posts')
    .delete()
    .eq('id', postId);

  if (error) throw error;
};

// Time Slots Management
export const getPostingSlots = async (projectId: string) => {
  const { data, error } = await supabase
    .from('reddit_posting_slots')
    .select('*')
    .eq('project_id', projectId)
    .order('day_of_week', { ascending: true })
    .order('time_slot_start_utc', { ascending: true }); // Use correct database field name

  if (error) throw error;
  return data || [];
};

export const savePostingSlots = async (
  projectId: string,
  userId: string,
  slots: Array<{
    day_of_week: number;
    hour: number;
    enabled?: boolean;
    timezone?: string;
  }>
) => {
  // Delete existing slots
  await supabase
    .from('reddit_posting_slots')
    .delete()
    .eq('project_id', projectId);

  // Insert new slots with correct field names
  const slotsWithIds = slots.map(slot => ({
    day_of_week: slot.day_of_week,
    time_slot_start_utc: `${slot.hour.toString().padStart(2, '0')}:00:00`, // Convert hour to time format
    is_active: slot.enabled !== false, // Use correct database field name
    project_id: projectId,
    // Note: user_id is not in the database schema for reddit_posting_slots
  }));

  const { data, error } = await supabase
    .from('reddit_posting_slots')
    .insert(slotsWithIds)
    .select();

  if (error) throw error;
  return data;
};

// Agent Log
export const getAgentLog = async (projectId: string, limit = 50) => {
  const { data, error } = await supabase
    .from('reddit_agent_log')
    .select('*')
    .eq('project_id', projectId)
    .order('run_timestamp_utc', { ascending: false })
    .limit(limit);

  if (error) throw error;
  return data || [];
};

// Agent Control Functions
export const getAgentSettings = async (projectId: string) => {
  const { data, error } = await supabase
    .from('projects')
    .select('*')
    .eq('id', projectId)
    .single();

  if (error) throw error;
  
  // Return default settings for now since the fields don't exist yet
  return {
    enable_reddit_agent: false,
    reddit_agent_paused: false
  };
};

export const updateAgentSettings = async (
  projectId: string,
  settings: {
    enable_reddit_agent?: boolean;
    reddit_agent_paused?: boolean;
  }
) => {
  // For now, just return success since the fields don't exist yet
  // This will need to be updated when we add the fields to the projects table
  return { success: true };
}; 